import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { authService } from '../services/api';
import { toast } from 'react-toastify';
import {
    HiOutlineCog,
    HiOutlineMoon,
    HiOutlineSun,
    HiOutlineUser,
    HiOutlineBell,
    HiOutlineGlobe,
    HiOutlineShieldCheck,
    HiOutlineColorSwatch,
    HiOutlineCamera,
    HiOutlineTrash,
    HiOutlineCheckCircle,
    HiOutlineEye,
    HiOutlineEyeOff,
    HiOutlineCheck,
    HiOutlineX
} from 'react-icons/hi';
import './Settings.css';

const Settings = () => {
    const { user, updateUser } = useAuth();
    const [theme, setTheme] = useState(() => {
        return user?.preferences?.theme || localStorage.getItem('theme') || 'light';
    });
    const [activeTab, setActiveTab] = useState('appearance');
    const [loading, setLoading] = useState(false);
    const [profileData, setProfileData] = useState({
        full_name: '',
        email: ''
    });
    const [profileImage, setProfileImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const fileInputRef = useRef(null);

    // Estados para cambio de contraseña
    const [passwordData, setPasswordData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });
    const [passwordValidations, setPasswordValidations] = useState({
        minLength: false,
        hasUpperCase: false,
        hasLowerCase: false,
        hasNumber: false,
        hasSpecialChar: false
    });
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:5001').replace(/\/api$/, '');

    useEffect(() => {
        document.documentElement.setAttribute('data-theme', theme);
        localStorage.setItem('theme', theme);
    }, [theme]);

    useEffect(() => {
        if (user) {
            setProfileData({
                full_name: user.full_name || '',
                email: user.email || ''
            });
            if (user.profile_image) {
                setImagePreview(`${API_URL}${user.profile_image}`);
            }
        }
    }, [user, API_URL]);

    const toggleTheme = () => {
        const newTheme = theme === 'light' ? 'dark' : 'light';
        setTheme(newTheme);
        authService.updatePreferences({ theme: newTheme })
            .then(res => {
                const updatedUser = { ...user, preferences: res.data.preferences };
                updateUser(updatedUser);
            })
            .catch(err => console.error('Error guardando preferencia de tema:', err));
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setProfileData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleImageSelect = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (!file.type.startsWith('image/')) {
                toast.error('Por favor selecciona una imagen válida');
                return;
            }
            if (file.size > 5 * 1024 * 1024) {
                toast.error('La imagen no debe superar 5MB');
                return;
            }
            setProfileImage(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleImageUpload = async () => {
        if (!profileImage) {
            toast.error('Por favor selecciona una imagen');
            return;
        }

        setLoading(true);
        try {
            const response = await authService.uploadProfilePhoto(profileImage);
            toast.success('Foto de perfil actualizada exitosamente');
            
            // Actualizar el contexto del usuario
            const updatedUser = { ...user, profile_image: response.data.profile_image };
            updateUser(updatedUser);
            
            setProfileImage(null);
        } catch (error) {
            console.error('Error al subir foto:', error);
            toast.error(error.response?.data?.error || 'Error al subir la foto de perfil');
        } finally {
            setLoading(false);
        }
    };

    const handleImageDelete = async () => {
        if (!window.confirm('¿Estás seguro de eliminar tu foto de perfil?')) {
            return;
        }

        setLoading(true);
        try {
            await authService.deleteProfilePhoto();
            toast.success('Foto de perfil eliminada exitosamente');
            
            // Actualizar el contexto del usuario
            const updatedUser = { ...user, profile_image: null };
            updateUser(updatedUser);
            
            setImagePreview(null);
            setProfileImage(null);
        } catch (error) {
            console.error('Error al eliminar foto:', error);
            toast.error(error.response?.data?.error || 'Error al eliminar la foto de perfil');
        } finally {
            setLoading(false);
        }
    };

    const handleProfileUpdate = async (e) => {
        e.preventDefault();
        
        if (!profileData.full_name.trim()) {
            toast.error('El nombre completo es requerido');
            return;
        }

        setLoading(true);
        try {
            const response = await authService.updateProfile(profileData);
            toast.success('Perfil actualizado exitosamente');
            
            // Actualizar el contexto del usuario
            updateUser(response.data.user);
        } catch (error) {
            console.error('Error al actualizar perfil:', error);
            toast.error(error.response?.data?.error || 'Error al actualizar el perfil');
        } finally {
            setLoading(false);
        }
    };

    // Validar contraseña en tiempo real
    const validatePassword = (password) => {
        setPasswordValidations({
            minLength: password.length >= 10,
            hasUpperCase: /[A-Z]/.test(password),
            hasLowerCase: /[a-z]/.test(password),
            hasNumber: /[0-9]/.test(password),
            hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password)
        });
    };

    const handlePasswordChange = (e) => {
        const { name, value } = e.target;
        setPasswordData(prev => ({
            ...prev,
            [name]: value
        }));

        // Validar en tiempo real solo la nueva contraseña
        if (name === 'newPassword') {
            validatePassword(value);
        }
    };

    const handlePasswordSubmit = async (e) => {
        e.preventDefault();

        // Validar que todos los campos estén llenos
        if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
            toast.error('Por favor completa todos los campos');
            return;
        }

        // Validar que todas las condiciones de seguridad se cumplan
        const allValidationsPassed = Object.values(passwordValidations).every(v => v === true);
        if (!allValidationsPassed) {
            toast.error('La nueva contraseña no cumple con todos los requisitos de seguridad');
            return;
        }

        // Validar que las contraseñas nuevas coincidan
        if (passwordData.newPassword !== passwordData.confirmPassword) {
            toast.error('Las contraseñas nuevas no coinciden');
            return;
        }

        setLoading(true);
        try {
            await authService.changePassword(passwordData.currentPassword, passwordData.newPassword);
            toast.success('Contraseña actualizada exitosamente');
            
            // Limpiar formulario
            setPasswordData({
                currentPassword: '',
                newPassword: '',
                confirmPassword: ''
            });
            setPasswordValidations({
                minLength: false,
                hasUpperCase: false,
                hasLowerCase: false,
                hasNumber: false,
                hasSpecialChar: false
            });
        } catch (error) {
            console.error('Error al cambiar contraseña:', error);
            toast.error(error.response?.data?.error || 'Error al cambiar la contraseña');
        } finally {
            setLoading(false);
        }
    };

    const tabs = [
        { id: 'appearance', label: 'Apariencia', icon: <HiOutlineColorSwatch /> },
        { id: 'profile', label: 'Perfil', icon: <HiOutlineUser /> },
        { id: 'notifications', label: 'Notificaciones', icon: <HiOutlineBell /> },
        { id: 'language', label: 'Idioma', icon: <HiOutlineGlobe /> },
        { id: 'security', label: 'Seguridad', icon: <HiOutlineShieldCheck /> }
    ];

    return (
        <div className="settings-page">
            <div className="settings-header">
                <div className="header-info">
                    <h1>
                        <HiOutlineCog />
                        Ajustes y Configuración
                    </h1>
                    <p>Personaliza tu experiencia en el CMS de Fenine</p>
                </div>
            </div>

            <div className="settings-container">
                <div className="settings-sidebar">
                    {tabs.map(tab => (
                        <button
                            key={tab.id}
                            className={`settings-tab ${activeTab === tab.id ? 'active' : ''}`}
                            onClick={() => setActiveTab(tab.id)}
                        >
                            {tab.icon}
                            <span>{tab.label}</span>
                        </button>
                    ))}
                </div>

                <div className="settings-content">
                    {activeTab === 'appearance' && (
                        <div className="settings-section">
                            <h2>Apariencia</h2>
                            <p className="section-description">
                                Personaliza cómo se ve el CMS según tus preferencias
                            </p>

                            <div className="setting-item">
                                <div className="setting-info">
                                    <div className="setting-label">
                                        {theme === 'light' ? <HiOutlineSun /> : <HiOutlineMoon />}
                                        <strong>Tema de color</strong>
                                    </div>
                                    <p className="setting-description">
                                        Cambia entre modo claro y oscuro
                                    </p>
                                </div>
                                <div className="theme-toggle">
                                    <button
                                        className={`theme-option ${theme === 'light' ? 'active' : ''}`}
                                        onClick={() => setTheme('light')}
                                    >
                                        <HiOutlineSun />
                                        <span>Claro</span>
                                    </button>
                                    <button
                                        className={`theme-option ${theme === 'dark' ? 'active' : ''}`}
                                        onClick={() => setTheme('dark')}
                                    >
                                        <HiOutlineMoon />
                                        <span>Oscuro</span>
                                    </button>
                                </div>
                            </div>

                            <div className="setting-item">
                                <div className="setting-info">
                                    <div className="setting-label">
                                        <HiOutlineColorSwatch />
                                        <strong>Vista previa</strong>
                                    </div>
                                    <p className="setting-description">
                                        Visualiza cómo se ve tu tema seleccionado
                                    </p>
                                </div>
                                <div className="theme-preview">
                                    <div className="preview-card">
                                        <div className="preview-header"></div>
                                        <div className="preview-sidebar">
                                            <div className="preview-nav-item"></div>
                                            <div className="preview-nav-item active"></div>
                                            <div className="preview-nav-item"></div>
                                        </div>
                                        <div className="preview-content">
                                            <div className="preview-line"></div>
                                            <div className="preview-line short"></div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'profile' && (
                        <div className="settings-section">
                            <h2>Información del Perfil</h2>
                            <p className="section-description">
                                Gestiona tu información personal y foto de perfil
                            </p>

                            {/* Sección de foto de perfil */}
                            <div className="profile-photo-section">
                                <h3>Foto de Perfil</h3>
                                <div className="profile-photo-container">
                                    <div className="profile-photo-preview">
                                        {imagePreview ? (
                                            <img src={imagePreview} alt="Perfil" />
                                        ) : (
                                            <div className="profile-avatar-placeholder">
                                                {user?.full_name?.[0] || 'U'}
                                            </div>
                                        )}
                                    </div>
                                    <div className="profile-photo-actions">
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            accept="image/*"
                                            onChange={handleImageSelect}
                                            style={{ display: 'none' }}
                                        />
                                        <button
                                            type="button"
                                            className="btn btn-secondary btn-sm"
                                            onClick={() => fileInputRef.current?.click()}
                                            disabled={loading}
                                        >
                                            <HiOutlineCamera />
                                            Seleccionar Imagen
                                        </button>
                                        {profileImage && (
                                            <button
                                                type="button"
                                                className="btn btn-primary btn-sm"
                                                onClick={handleImageUpload}
                                                disabled={loading}
                                            >
                                                <HiOutlineCheckCircle />
                                                {loading ? 'Subiendo...' : 'Guardar Foto'}
                                            </button>
                                        )}
                                        {user?.profile_image && !profileImage && (
                                            <button
                                                type="button"
                                                className="btn btn-danger btn-sm"
                                                onClick={handleImageDelete}
                                                disabled={loading}
                                            >
                                                <HiOutlineTrash />
                                                Eliminar
                                            </button>
                                        )}
                                    </div>
                                    <p className="photo-hint">
                                        Formatos: JPG, PNG, GIF. Tamaño máximo: 5MB
                                    </p>
                                </div>
                            </div>

                            {/* Formulario de información personal */}
                            <form onSubmit={handleProfileUpdate} className="profile-form">
                                <h3>Información Personal</h3>
                                
                                <div className="form-group">
                                    <label className="form-label">
                                        Nombre Completo <span className="required">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="full_name"
                                        className="form-control"
                                        value={profileData.full_name}
                                        onChange={handleInputChange}
                                        required
                                        disabled={loading}
                                    />
                                </div>

                                <div className="form-group">
                                    <label className="form-label">
                                        Correo Electrónico <span className="required">*</span>
                                    </label>
                                    <input
                                        type="email"
                                        name="email"
                                        className="form-control"
                                        value={profileData.email}
                                        onChange={handleInputChange}
                                        required
                                        disabled={loading}
                                    />
                                    <small className="form-hint">
                                        Este correo se usará para iniciar sesión
                                    </small>
                                </div>

                                <div className="form-group">
                                    <label className="form-label">Usuario</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={user?.username || ''}
                                        disabled
                                    />
                                    <small className="form-hint">
                                        El nombre de usuario no se puede modificar
                                    </small>
                                </div>

                                <div className="form-group">
                                    <label className="form-label">Rol</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={user?.role || ''}
                                        disabled
                                    />
                                    <small className="form-hint">
                                        Contacta al administrador para cambiar tu rol
                                    </small>
                                </div>

                                <div className="form-actions">
                                    <button
                                        type="submit"
                                        className="btn btn-primary"
                                        disabled={loading}
                                    >
                                        <HiOutlineCheckCircle />
                                        {loading ? 'Guardando...' : 'Guardar Cambios'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}

                    {activeTab === 'notifications' && (
                        <div className="settings-section">
                            <h2>Notificaciones</h2>
                            <p className="section-description">
                                Configura cómo quieres recibir notificaciones
                            </p>

                            <div className="info-message">
                                <HiOutlineBell />
                                <p>Las configuraciones de notificaciones estarán disponibles próximamente.</p>
                            </div>
                        </div>
                    )}

                    {activeTab === 'language' && (
                        <div className="settings-section">
                            <h2>Idioma</h2>
                            <p className="section-description">
                                Selecciona tu idioma preferido
                            </p>

                            <div className="info-message">
                                <HiOutlineGlobe />
                                <p>Actualmente el sistema está disponible en Español. Más idiomas próximamente.</p>
                            </div>
                        </div>
                    )}

                    {activeTab === 'security' && (
                        <div className="settings-section">
                            <h2>Seguridad</h2>
                            <p className="section-description">
                                Protege tu cuenta y datos
                            </p>

                            <div className="security-section-card">
                                <h3>Cambiar Contraseña</h3>
                                <p className="section-hint">
                                    Mantén tu cuenta segura con una contraseña fuerte
                                </p>

                                <form onSubmit={handlePasswordSubmit} className="password-form">
                                    <div className="form-group">
                                        <label className="form-label">Contraseña Actual</label>
                                        <div className="password-input-wrapper">
                                            <input
                                                type={showCurrentPassword ? "text" : "password"}
                                                name="currentPassword"
                                                className="form-control"
                                                value={passwordData.currentPassword}
                                                onChange={handlePasswordChange}
                                                placeholder="Tu contraseña actual"
                                                disabled={loading}
                                            />
                                            <button
                                                type="button"
                                                className="password-toggle-btn"
                                                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                            >
                                                {showCurrentPassword ? <HiOutlineEyeOff /> : <HiOutlineEye />}
                                            </button>
                                        </div>
                                    </div>

                                    <div className="form-group">
                                        <label className="form-label">Nueva Contraseña</label>
                                        <div className="password-input-wrapper">
                                            <input
                                                type={showNewPassword ? "text" : "password"}
                                                name="newPassword"
                                                className="form-control"
                                                value={passwordData.newPassword}
                                                onChange={handlePasswordChange}
                                                placeholder="Tu nueva contraseña"
                                                disabled={loading}
                                            />
                                            <button
                                                type="button"
                                                className="password-toggle-btn"
                                                onClick={() => setShowNewPassword(!showNewPassword)}
                                            >
                                                {showNewPassword ? <HiOutlineEyeOff /> : <HiOutlineEye />}
                                            </button>
                                        </div>

                                        {/* Validaciones visuales */}
                                        {passwordData.newPassword && (
                                            <div className="password-validations">
                                                <div className={`validation-item ${passwordValidations.minLength ? 'valid' : 'invalid'}`}>
                                                    {passwordValidations.minLength ? <HiOutlineCheck /> : <HiOutlineX />}
                                                    <span>Mínimo 10 caracteres</span>
                                                </div>
                                                <div className={`validation-item ${passwordValidations.hasUpperCase ? 'valid' : 'invalid'}`}>
                                                    {passwordValidations.hasUpperCase ? <HiOutlineCheck /> : <HiOutlineX />}
                                                    <span>Al menos una letra mayúscula (A-Z)</span>
                                                </div>
                                                <div className={`validation-item ${passwordValidations.hasLowerCase ? 'valid' : 'invalid'}`}>
                                                    {passwordValidations.hasLowerCase ? <HiOutlineCheck /> : <HiOutlineX />}
                                                    <span>Al menos una letra minúscula (a-z)</span>
                                                </div>
                                                <div className={`validation-item ${passwordValidations.hasNumber ? 'valid' : 'invalid'}`}>
                                                    {passwordValidations.hasNumber ? <HiOutlineCheck /> : <HiOutlineX />}
                                                    <span>Al menos un número (0-9)</span>
                                                </div>
                                                <div className={`validation-item ${passwordValidations.hasSpecialChar ? 'valid' : 'invalid'}`}>
                                                    {passwordValidations.hasSpecialChar ? <HiOutlineCheck /> : <HiOutlineX />}
                                                    <span>Al menos un carácter especial (!@#$%...)</span>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <div className="form-group">
                                        <label className="form-label">Confirmar Nueva Contraseña</label>
                                        <div className="password-input-wrapper">
                                            <input
                                                type={showConfirmPassword ? "text" : "password"}
                                                name="confirmPassword"
                                                className="form-control"
                                                value={passwordData.confirmPassword}
                                                onChange={handlePasswordChange}
                                                placeholder="Repite tu nueva contraseña"
                                                disabled={loading}
                                            />
                                            <button
                                                type="button"
                                                className="password-toggle-btn"
                                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                            >
                                                {showConfirmPassword ? <HiOutlineEyeOff /> : <HiOutlineEye />}
                                            </button>
                                        </div>
                                        {passwordData.confirmPassword && (
                                            <div className={`password-match ${passwordData.newPassword === passwordData.confirmPassword ? 'match' : 'no-match'}`}>
                                                {passwordData.newPassword === passwordData.confirmPassword ? (
                                                    <>
                                                        <HiOutlineCheck />
                                                        <span>Las contraseñas coinciden</span>
                                                    </>
                                                ) : (
                                                    <>
                                                        <HiOutlineX />
                                                        <span>Las contraseñas no coinciden</span>
                                                    </>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    <div className="form-actions">
                                        <button
                                            type="submit"
                                            className="btn btn-primary"
                                            disabled={loading}
                                        >
                                            <HiOutlineShieldCheck />
                                            {loading ? 'Cambiando...' : 'Cambiar Contraseña'}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Settings;
